import { readSetting, writeSetting } from '../../infrastructure/storage/localStore';

/**
 * What this device remembers about being offered the install.
 *
 * Three numbers, and all three exist to keep an offer from becoming a nag: how
 * many times this browser has opened the app, when the card was waved away, and
 * whether the app has already been installed. The policy in
 * `domain/pwa/installOffer.js` decides what the numbers mean; this module only
 * remembers them.
 *
 * Preferences, not data — nothing here is worth syncing and nothing here is
 * sensitive, which is what makes Web Storage the right home for it.
 */

export const VISIT_COUNT_KEY = 'pwa.visits';
export const INSTALL_DISMISSED_KEY = 'pwa.installDismissedAt';

/**
 * That this browser has installed the app — which is NOT the same as a dismissal
 * and cannot be held in component state.
 *
 * The card unmounts on a car detail page and the offer is re-derived on the way
 * back, so a "hidden for now" flag in React is lost by an ordinary navigation.
 * The tab that DID the installing is also still not `display-mode: standalone`,
 * so the support state falls back to the browser-menu instruction — and the card
 * returns, telling somebody who has just installed the app to go and install it.
 */
export const INSTALL_COMPLETED_KEY = 'pwa.installedAt';

/**
 * A visit is an app START, not a navigation.
 *
 * This app is a single page: every route change keeps the same JavaScript
 * running, so counting anything finer would turn one curious afternoon into
 * "twenty visits" and offer to install itself on the way. The flag is module
 * scope for exactly that reason — it resets when the page does, and not before.
 */
let hasCountedThisSession = false;

/**
 * A reload is the same visit, whoever asked for it.
 *
 * The app reloads BY ITSELF whenever a deploy lands and the reader is
 * backgrounded or idle. Counting that would let the app manufacture the "they
 * came back" signal the install offer waits for — they never left, and the card
 * would arrive because we shipped rather than because they returned.
 */
function isReload(windowObject) {
    try {
        const [navigation] = windowObject.performance.getEntriesByType('navigation');

        return Boolean(navigation) && navigation.type === 'reload';
    } catch (error) {
        // No Navigation Timing Level 2 — count it, which is what this would
        // always have done.
        return false;
    }
}

function readNumber(key) {
    const parsed = Number.parseInt(readSetting(key), 10);

    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function readVisitCount() {
    return readNumber(VISIT_COUNT_KEY);
}

/** Idempotent within a page lifetime; returns the count including this visit. */
export function countVisit(windowObject = typeof window === 'undefined' ? null : window) {
    if (hasCountedThisSession) {
        return readVisitCount();
    }

    hasCountedThisSession = true;

    if (windowObject && isReload(windowObject)) {
        return readVisitCount();
    }

    const nextCount = readVisitCount() + 1;

    writeSetting(VISIT_COUNT_KEY, String(nextCount));

    return nextCount;
}

export function readInstallDismissedAt() {
    return readNumber(INSTALL_DISMISSED_KEY);
}

export function writeInstallDismissed(now = Date.now()) {
    return writeSetting(INSTALL_DISMISSED_KEY, String(now));
}

export function readInstallCompletedAt() {
    return readNumber(INSTALL_COMPLETED_KEY);
}

export function writeInstallCompleted(now = Date.now()) {
    return writeSetting(INSTALL_COMPLETED_KEY, String(now));
}

/** Test seam — the session guard would otherwise leak one spec's visit into the next. */
export function resetVisitSessionGuard() {
    hasCountedThisSession = false;
}
